import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Row, { RowBetween } from '@/components/Row';
import { AutoColumn } from '@/components/Column';
import { TYPE } from '@/theme';
import NetworkSelect from '@/components/NetworkSelect';
import Modal from '@/components/Modal';
import { ChainId, NETWORK_CONFIG, Token, TokenLists } from '../../constants';
import BigNumber from 'bignumber.js';
import AlertFunc from '@/utils/components/alert';
import useWeb3SwitchNetwork from '@/hooks/use-web3-switch-network';
import ThirdPartyList from '../homepage/thirdPartyList';
import Noti from '../homepage/Noti';
import user from '@/stores/user';
import { observer } from 'mobx-react';
import SwitchSVG from '@/assets/images/ic_switch.svg';
import TipSVG from '@/assets/images/tips.svg';

// Import bridge components
import StandardBridge from './bridges/StandardBridge';
import MetisToGoatBridge from './bridges/MetisToGoatBridge';
import GoatToMetisBridge from './bridges/GoatToMetisBridge';

const Wrapper = styled.div`
    width: 1000px;

    box-sizing: border-box;
    margin: 0 auto;
    ${({ theme }) => theme.mediaWidth.upToSmall`
    width: calc(100vw - 2rem);
    `}
    .infoIcon {
        cursor: pointer;
        width: 24px;
        height: 24px;
        background-image: url(${TipSVG});
        background-size: 100%;
        background-position: center;
        background-repeat: no-repeat;
        ${({ theme }) => theme.mediaWidth.upToSmall`
        margin-top: 0;
        `}
    }
    .historylink {
        font-size: 14px;
        color: ${({ theme }) => theme.c.t04};
        line-height: 19px;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const PageWrapper = styled.div`
    position: relative;
    display: block;

    width: fit-content;
    margin: 0 auto;

    ${({ theme }) => theme.mediaWidth.upToSmall`
        width: 100%;
    `}

    .innerContainer {
        position: relative;
        width: 540px;
        background: ${({ theme }) => theme.c.bg05};
        border-radius: 48px;
        box-sizing: border-box;
        padding: 30px;
        overflow: hidden;
        box-shadow: 1px 0px 24px 4px rgba(7, 221, 208, 0.2);
        ${({ theme }) => theme.mediaWidth.upToSmall`
        width: 100%;
        border-radius: 24px;
        padding: 1rem;
        `};
    }

    .shadow-top {
        position: absolute;
        top: 0;
        bottom: 50%;
        left: 0;
        right: 0;
        box-shadow: 1px 0px 96px 1px rgba(7, 221, 208, 0.24);
        border-radius: 170px 170px 0 0;
        z-index: -1;
    }

    .shadow-bottom {
        position: absolute;
        bottom: 0;
        left: 0;
        top: 50%;
        right: 0;
        box-shadow: 1px 0px 96px 1px rgba(255, 0, 199, 0.32);
        border-radius: 0 0 170px 170px;
        z-index: -1;
    }
`;

const SwtichNetworkWrapper = styled(RowBetween)`
    background: ${({ theme }) => theme.c.bg04};
    margin-top: 16px;

    border-radius: 40px;
    box-sizing: border-box;
    padding: 16px;
    height: 80px;
    gap: 8px;
    ${({ theme }) => theme.mediaWidth.upToSmall`
        height: fit-content;
        padding: 4px 8px ;
        margin-top: 8px;
    `};
`;

const SwtichIcon = styled.div`
    background-repeat: no-repeat;
    background-size: 100%;
    background-position: center center;
    background-image: url(${SwitchSVG});
    width: 44px;
    height: 36px;
    ${({ theme }) => theme.mediaWidth.upToSmall`
    margin-top: 8px;
    width: 36px;
    height: 27px;
    `}
`;

const InputPanel = styled.div`
    width: 100%;
    margin-top: 24px;
    ${({ theme }) => theme.mediaWidth.upToSmall`
    margin-top: 12px;
    `}
`;

const SwitchNetworkHeader = styled(RowBetween)`
    margin-top: 24px;
    ${({ theme }) => theme.mediaWidth.upToSmall`
    margin-top: 12px;
    `}
`;

const AirdropImg = styled.img`
    width: 540px;
    height: auto;
    margin-top: 12px;
    margin-bottom: 32px;
    cursor: pointer;
    ${({ theme }) => theme.mediaWidth.upToSmall`
    display: none;
    `};
`;

const AirdropImgMobile = styled.img`
    display: none;
    width: calc(100vw - 2rem);
    height: auto;
    margin-bottom: 2rem;
    ${({ theme }) => theme.mediaWidth.upToSmall`
    display: block;
    `}
`;

const DisclaimerWrapper = styled.div`
    width: 464px;
    box-sizing: border-box;
    ${({ theme }) => theme.mediaWidth.upToSmall`
    width: 100%;
    height: fit-content;
`}
`;

function isValidPip(from: ChainId, to: ChainId) {
    const validChainPip = Object.keys(TokenLists);
    const key = `${from}_${to}`;
    return validChainPip.includes(key);
}

function getDeafaultToChainId(chainId: ChainId) {
    const validChainPip = Object.keys(TokenLists);

    const filterPairs = validChainPip.filter((item) => {
        return item.split('_')[0] === `${chainId}`;
    });

    const [, toChainId] = filterPairs[0].split('_');
    return Number(toChainId) as ChainId;
}

function getDeafaultToken(from: ChainId, to: ChainId) {
    if (!isValidPip(from, to)) {
        const toChain = getDeafaultToChainId(from);
        return TokenLists[`${from}_${toChain}`][0];
    }
    return TokenLists[`${from}_${to}`][0];
}

function HomepageV2() {
    const { chainId } = user;

    const { handleChangeNetwork } = useWeb3SwitchNetwork();
    const [fromChainId, setFromChainId] = useState(chainId);
    const [toChainId, setToChainId] = useState(getDeafaultToChainId(chainId));
    const [currentTokenKey, setCurrentToken] = useState<Token>();

    const currentToken = useMemo(() => {
        if (currentTokenKey) return currentTokenKey;
        return getDeafaultToken(fromChainId, toChainId);
    }, [currentTokenKey, fromChainId, toChainId]);

    const [disclaimerModalOpen, setDisclaimerModalOpen] = useState(false);

    useEffect(() => {
        setCurrentToken(undefined);
        setFromChainId(chainId);
    }, [chainId]);

    function transactionSuccess(
        tx: string,
        amount: BigNumber,
        tokenName: string,
    ) {
        AlertFunc({
            anchorOrigin: {
                horizontal: 'right',
                vertical: 'top',
            },
            key: `${Math.random()}`,
            text: (
                <>
                    <Row lineHeight="none">
                        Transfer {amount.toNumber()} {tokenName} to{' '}
                        {NETWORK_CONFIG[toChainId].name}
                    </Row>
                    <Row lineHeight="none">
                        <a
                            href={`${NETWORK_CONFIG[fromChainId].explore}/tx/${tx}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            View on {NETWORK_CONFIG[fromChainId].scanLable}
                        </a>
                    </Row>
                </>
            ),
            type: 'success',
            duration: 8000,
        });
    }

    async function handleFromChainIdChange(changeChainId: ChainId) {
        setFromChainId(changeChainId);
        setToChainId(chainId);
        await handleChangeNetwork(changeChainId);
    }

    const listChains = useMemo(() => {
        switch (fromChainId) {
            case ChainId.mainnet:
                return [ChainId.metisMainnet];
            case ChainId.metisMainnet:
                return [ChainId.mainnet, ChainId.goatedMainnet];
            case ChainId.goatedMainnet:
                return [ChainId.metisMainnet];
            case ChainId.testnet:
                return [ChainId.metisTestnet];
            case ChainId.metisTestnet:
                return [ChainId.testnet];
            default:
                return [];
        }
    }, [fromChainId]);

    useEffect(() => {
        const validChainPip = Object.keys(TokenLists);
        const key = `${fromChainId}_${toChainId}`;
        if (!validChainPip.includes(key)) {
            setToChainId(getDeafaultToChainId(fromChainId));
        }
    }, [fromChainId, toChainId]);

    // Determine which bridge component to render
    function renderBridgeComponent() {
        // Metis -> GOAT (LayerZero)
        if (
            fromChainId === ChainId.metisMainnet &&
            toChainId === ChainId.goatedMainnet
        ) {
            return (
                <MetisToGoatBridge
                    fromChainId={fromChainId}
                    toChainId={toChainId}
                    currentToken={currentToken}
                    setCurrentToken={setCurrentToken}
                    onTransactionSuccess={transactionSuccess}
                />
            );
        }

        // GOAT -> Metis (LayerZero)
        if (
            fromChainId === ChainId.goatedMainnet &&
            toChainId === ChainId.metisMainnet
        ) {
            return (
                <GoatToMetisBridge
                    fromChainId={fromChainId}
                    toChainId={toChainId}
                    currentToken={currentToken}
                    setCurrentToken={setCurrentToken}
                    onTransactionSuccess={transactionSuccess}
                />
            );
        }

        // Standard bridge (Ethereum/Sepolia/Metis)
        return (
            <StandardBridge
                fromChainId={fromChainId}
                toChainId={toChainId}
                currentToken={currentToken}
                setCurrentToken={setCurrentToken}
                onTransactionSuccess={transactionSuccess}
            />
        );
    }

    return (
        <Wrapper>
            <Noti />
            <PageWrapper>
                <div className="shadow-top" />
                <div className="shadow-bottom" />
                <div className="innerContainer">
                    <Row
                        justify="center"
                        style={{
                            flexDirection: 'column',
                            position: 'relative',
                        }}
                    >
                        <TYPE.main
                            fontSize={18}
                            fontWeight={600}
                            letterSpacing={2}
                            color="t03"
                            lineHeight={1}
                        >
                            BRIDGE
                        </TYPE.main>
                        <TYPE.main
                            fontSize={22}
                            fontWeight={600}
                            letterSpacing={2}
                            lineHeight={1.2}
                        >
                            TOKENS
                        </TYPE.main>
                    </Row>
                    <SwitchNetworkHeader>
                        <TYPE.main fontSize={14} fontWeight={500}>
                            FROM NETWORK
                        </TYPE.main>
                        <TYPE.main fontSize={14} fontWeight={500}>
                            TO NETWORK
                        </TYPE.main>
                    </SwitchNetworkHeader>
                    <SwtichNetworkWrapper>
                        <NetworkSelect
                            chainId={fromChainId}
                            setChainId={(chainId) => {
                                handleFromChainIdChange(chainId);
                            }}
                        />
                        <AutoColumn
                            justify="center"
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                handleFromChainIdChange(toChainId);
                            }}
                        >
                            <SwtichIcon />
                            <TYPE.main fontSize={12}>Switch</TYPE.main>
                        </AutoColumn>
                        <NetworkSelect
                            chainId={toChainId}
                            setChainId={setToChainId}
                            chains={listChains}
                        />
                    </SwtichNetworkWrapper>
                    <InputPanel>{renderBridgeComponent()}</InputPanel>
                </div>
            </PageWrapper>
            <div style={{ textAlign: 'center', marginTop: 20 }}>
                <AirdropImg
                    src={require('@/assets/images/activity/airdrop-gas.png')}
                    onClick={() => {
                        setDisclaimerModalOpen(true);
                    }}
                />
                <AirdropImgMobile
                    onClick={() => {
                        setDisclaimerModalOpen(true);
                    }}
                    src={require('@/assets/images/activity/airdrop-gas-mobile.png')}
                />
                <ThirdPartyList
                    isDeposit={
                        fromChainId === ChainId.mainnet ||
                        fromChainId === ChainId.testnet
                    }
                    isMetis={currentToken === Token.metis}
                />
                {(fromChainId === ChainId.mainnet ||
                    fromChainId === ChainId.metisMainnet ||
                    fromChainId === ChainId.goatedMainnet) &&
                    user.connected && (
                        <Link
                            className={'historylink'}
                            to={'history'}
                        >{`View your transaction history >`}</Link>
                    )}
            </div>
            <Modal
                isOpen={disclaimerModalOpen}
                onClose={() => {
                    setDisclaimerModalOpen(false);
                }}
            >
                <DisclaimerWrapper>
                    <TYPE.main
                        fontSize={16}
                        textAlign="center"
                        fontWeight="bold"
                    >
                        Gas Fee Airdrop Rules
                    </TYPE.main>
                    <AutoColumn style={{ marginTop: '36px' }} gap="md">
                        <TYPE.main fontSize={16} fontWeight={600}>
                            • New wallets (no prior interaction with Andromeda
                            network)
                        </TYPE.main>
                        <TYPE.main fontSize={16} fontWeight={600}>
                            • Eligible tokens USDC, USDT, DAI, ETH, wBTC, Link,
                            AAVE, BUSD, Sushi (except Metis Token)
                        </TYPE.main>
                        <TYPE.main fontSize={16} fontWeight={600}>
                            • Min value amount $100 bridging into the Andromeda
                            Network
                        </TYPE.main>
                        <TYPE.main fontSize={16} fontWeight={600}>
                            • 0.01 Metis Token will be airdropped to the wallet
                            on Andromeda Network
                        </TYPE.main>
                        <TYPE.main fontSize={16} fontWeight={600}>
                            • You can use the airdropped Metis Token as the gas
                            fee to complete a few transactions on Andromeda
                            Network
                        </TYPE.main>
                    </AutoColumn>
                </DisclaimerWrapper>
            </Modal>
        </Wrapper>
    );
}

export default observer(HomepageV2);
